import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  CreditCard,
  Heart,
  CheckCircle2,
  Upload,
  Globe,
  ArrowLeft,
  Loader2,
} from 'lucide-react';

export function UpgradePage() {
  const navigate = useNavigate();
  const { config, refreshConfig } = useBusinessConfig();
  const [verifying, setVerifying] = useState(false);
  const [ndisNumber, setNdisNumber] = useState('');
  const [ndisDocument, setNdisDocument] = useState('');

  const isTrial = config?.subscription_status === 'trial';
  const trialDaysLeft = config?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(config.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const handleStripeCheckout = async (plan: 'starter' | 'pro') => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    // Check if 100% discount is applied - bypass Stripe
    if (config?.discount_percent === 100) {
      // Grant free access directly
      const { error } = await supabase
        .from('business_config')
        .update({
          subscription_status: 'active',
          plan,
        })
        .eq('user_id', user.id);

      if (error) {
        toast.error('Failed to activate free access');
        return;
      }

      toast.success('Free access activated! Welcome to ' + plan + '!');
      refreshConfig();
      navigate('/admin');
      return;
    }

    // Placeholder Stripe Payment Links
    const stripeLinks = {
      starter: 'https://buy.stripe.com/PLACEHOLDER_STARTER',
      pro: 'https://buy.stripe.com/PLACEHOLDER_PRO',
    };

    window.open(stripeLinks[plan], '_blank');
    toast.success('Redirecting to secure checkout...');
  };

  const handleNdisVerification = async () => {
    if (!ndisNumber.trim()) {
      toast.error('Please enter your NDIS number');
      return;
    }

    setVerifying(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      // Apply 50% discount for NDIS verified users
      const { error } = await supabase
        .from('business_config')
        .update({
          ndis_verified: true,
          ndis_discount_applied: true,
          discount_percent: 50,
          discount_reason: 'NDIS Verified',
        })
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('NDIS verification submitted! 50% discount will be applied within 24 hours.');
      refreshConfig();
    } catch (error) {
      console.error('NDIS verification error:', error);
      toast.error('Failed to submit verification');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-black text-slate-900 uppercase">
              {isTrial ? 'Upgrade Your Plan' : 'Restore Access'}
            </h1>
            {isTrial && (
              <p className="text-slate-600 text-sm mt-1">
                {trialDaysLeft > 7
                  ? `You have ${trialDaysLeft} days left in your trial`
                  : trialDaysLeft > 0
                  ? `⚡ Trial expires in ${trialDaysLeft} days — upgrade now!`
                  : 'Your trial has ended'}
              </p>
            )}
          </div>
        </div>

        {/* Current Plan Status */}
        {config && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase">Current Plan</p>
                <p className="text-2xl font-black text-slate-900 uppercase">
                  {config.plan === 'pro' ? 'Pro' : config.subscription_status === 'trial' ? 'Trial' : 'Starter'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-slate-400 uppercase">Status</p>
                <p className={`text-lg font-black uppercase ${
                  config.subscription_status === 'active' ? 'text-green-600' :
                  config.subscription_status === 'trial' ? 'text-amber-600' :
                  'text-red-600'
                }`}>
                  {config.subscription_status}
                </p>
                {config.discount_percent && config.discount_percent > 0 && (
                  <p className="text-xs font-black text-pink-600 mt-1">
                    {config.discount_percent}% discount applied
                    {config.discount_reason && ` (${config.discount_reason})`}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Starter Plan */}
          <div className="bg-white border-2 border-slate-100 rounded-[2rem] p-8 shadow-sm hover:border-purple-300 transition-colors">
            <div>
              <h3 className="text-2xl font-black text-slate-900 uppercase">Starter</h3>
              <p className="text-slate-500 text-sm mt-1">Perfect for solo operators</p>
            </div>
            <div className="flex items-baseline gap-1 mt-4">
              {config?.discount_percent === 100 ? (
                <>
                  <span className="text-5xl font-black text-pink-600">FREE</span>
                </>
              ) : config?.discount_percent === 50 ? (
                <>
                  <span className="text-3xl font-black text-slate-400 line-through">$19</span>
                  <span className="text-5xl font-black text-pink-600">$9.50</span>
                  <span className="text-slate-500 font-bold">/month AUD</span>
                </>
              ) : (
                <>
                  <span className="text-5xl font-black text-slate-900">$19</span>
                  <span className="text-slate-500 font-bold">/month AUD</span>
                </>
              )}
            </div>
            <ul className="space-y-3 mt-6">
              <li className="flex items-center gap-2 text-slate-700 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-600" />
                Up to 30 customers
              </li>
              <li className="flex items-center gap-2 text-slate-700 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-600" />
                Unlimited quotes & invoices
              </li>
              <li className="flex items-center gap-2 text-slate-700 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-600" />
                Inventory management
              </li>
              <li className="flex items-center gap-2 text-slate-700 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-600" />
                Booking system
              </li>
            </ul>
            {/* Stripe Buy Button - Starter Plan (Australia Only) */}
            <div className="mt-6">
              <a
                href="https://buy.stripe.com/?prefilled_email=0"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-black uppercase tracking-widest text-center leading-[3.5rem]"
              >
                Subscribe — $19/mo AUD
              </a>
              <p className="text-[9px] text-slate-500 font-bold uppercase mt-2 text-center">
                🇦🇺 Australia Only • Secure checkout via Stripe
              </p>
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-[2rem] p-8 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-black text-white uppercase">Pro</h3>
              <span className="text-[10px] font-black bg-white/20 text-white px-2 py-1 rounded uppercase">
                Most Popular
              </span>
            </div>
            <div className="flex items-baseline gap-1 mt-4">
              {config?.discount_percent === 100 ? (
                <>
                  <span className="text-5xl font-black text-white">FREE</span>
                </>
              ) : config?.discount_percent === 50 ? (
                <>
                  <span className="text-3xl font-black text-white/60 line-through">$49</span>
                  <span className="text-5xl font-black text-white">$24.50</span>
                  <span className="text-white/70 font-bold">/month AUD</span>
                </>
              ) : (
                <>
                  <span className="text-5xl font-black text-white">$49</span>
                  <span className="text-white/70 font-bold">/month AUD</span>
                </>
              )}
            </div>
            <ul className="space-y-3 mt-6">
              <li className="flex items-center gap-2 text-white text-sm">
                <CheckCircle2 className="w-4 h-4 text-white" />
                Unlimited customers
              </li>
              <li className="flex items-center gap-2 text-white text-sm">
                <CheckCircle2 className="w-4 h-4 text-white" />
                Everything in Starter
              </li>
              <li className="flex items-center gap-2 text-white text-sm">
                <CheckCircle2 className="w-4 h-4 text-white" />
                Priority support
              </li>
              <li className="flex items-center gap-2 text-white text-sm">
                <CheckCircle2 className="w-4 h-4 text-white" />
                Advanced analytics
              </li>
            </ul>
            {/* Stripe Buy Button - Pro Plan (Australia Only) */}
            <div className="mt-6">
              <p className="text-[10px] text-white/70 font-bold uppercase mb-2 text-center">
                Coming Soon • Australia Only
              </p>
              <Button
                onClick={() => handleStripeCheckout('pro')}
                className="w-full h-14 bg-white text-purple-900 hover:bg-purple-50 rounded-2xl font-black uppercase tracking-widest"
                disabled
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Pro Plan (Soon)
              </Button>
            </div>
          </div>
        </div>

        {/* NDIS Verification */}
        <div className="bg-white border-2 border-pink-200 rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-6 h-6 text-pink-500" />
            <h3 className="font-black text-slate-900 uppercase">NDIS & PWD Discount</h3>
          </div>
          <p className="text-slate-600 text-sm mb-6">
            Verified NDIS participants and pensioners receive <span className="font-black text-pink-600">50% off</span> or 
            <span className="font-black text-pink-600"> free access</span>. Upload your NDIS card or pension card for verification.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                NDIS Number (Optional)
              </label>
              <Input
                value={ndisNumber}
                onChange={(e) => setNdisNumber(e.target.value)}
                placeholder="e.g. 123456789"
                className="h-12 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                Upload Document (Optional)
              </label>
              <div className="flex gap-2">
                <Input
                  value={ndisDocument}
                  onChange={(e) => setNdisDocument(e.target.value)}
                  placeholder="Paste URL to document or NDIS card image"
                  className="h-12 rounded-xl flex-1"
                />
                <Button variant="outline" className="rounded-xl">
                  <Upload className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <Button
              onClick={handleNdisVerification}
              disabled={verifying}
              className="w-full h-14 bg-pink-500 hover:bg-pink-600 text-white rounded-2xl font-black uppercase tracking-widest"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit for Verification'
              )}
            </Button>
            <p className="text-[9px] text-slate-500 text-center font-bold uppercase">
              Manual verification takes 24-48 hours. Discount applied retroactively.
            </p>
          </div>
        </div>

        {/* International Users - Whop */}
        <div className="bg-slate-100 rounded-2xl p-6 text-center">
          <Globe className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <h4 className="font-black text-slate-900 uppercase mb-2">Outside Australia?</h4>
          <p className="text-slate-600 text-sm mb-4">
            International customers can purchase via Whop for instant access.
          </p>
          <div className="space-y-3">
            {/* Whop Starter - $19.99 USD/mo */}
            <a
              href="https://whop.com/checkout/plan_kP4RvQFtMoGg1"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-purple-400 transition-colors"
            >
              <p className="text-[10px] font-black text-slate-500 uppercase mb-1">Starter Plan</p>
              <p className="text-xl font-black text-slate-900">$19.99 <span className="text-sm font-bold text-slate-500">USD/mo</span></p>
              <p className="text-[9px] text-purple-600 font-bold mt-2">🌏 Instant Access via Whop</p>
            </a>
            {/* Whop Pro - $79.99 USD/mo */}
            <a
              href="https://whop.com/checkout/plan_k2ZoNTlw72XQX"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-gradient-to-r from-purple-600 to-purple-800 border-2 border-purple-500 rounded-xl p-4 hover:from-purple-500 hover:to-purple-700 transition-colors"
            >
              <p className="text-[10px] font-black text-white/80 uppercase mb-1">Pro Plan</p>
              <p className="text-xl font-black text-white">$79.99 <span className="text-sm font-bold text-white/70">USD/mo</span></p>
              <p className="text-[9px] text-white font-bold mt-2">⭐ Best Value • Instant Access</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
